/**
 * Typed fetch wrapper for the Platterplan API.
 *
 * Security properties:
 *  - credentials: 'include' sends HttpOnly auth cookies automatically
 *  - CSRF token is read from the csrf_token cookie and sent as X-CSRF-Token header
 *  - Tokens are never stored in localStorage or sessionStorage
 *  - On 401, attempts one token refresh; if that fails, redirects to /login
 *    UNLESS silent:true is passed — in that case the error is thrown without redirect
 *    (used by public pages that need to check auth state without forcing navigation)
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) ?? '';
const STATE_CHANGING = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

// Auth endpoints that legitimately return 401 for bad credentials — skip the
// token-refresh interceptor so the server's error message surfaces correctly.
const NO_REFRESH_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/signup',
  '/api/auth/totp/validate',
]);

function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

let refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = fetch(`${BASE_URL}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  retried = false,
  silent = false,
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (STATE_CHANGING.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && !retried && !NO_REFRESH_PATHS.has(path)) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return request<T>(path, options, true, silent);
    }
    // Only hard-redirect on 401 if the caller wants it (the default).
    // Silent callers (e.g. public pages checking auth) receive the error instead.
    if (!silent) {
      window.location.href = '/login';
    }
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = Object.assign(new Error(body.error ?? 'Request failed'), {
      status: res.status,
      body,
    });
    throw err;
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, opts?: { silent?: boolean }) =>
    request<T>(path, {}, false, opts?.silent ?? false),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(data) }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(data) }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
